import type {FormInstance} from "antd";
import * as enums from "@interface/enums"
import type {IGridDecorator} from "@interface/IGridDecorator"
import React from "react";
import { EReports } from "@interface/enums";

export enum EFormLayout {
    horizontal = "horizontal",
    vertical = "vertical",
    tabs = "tabs"
}

export enum EEmitterEventName {
    layoutStyleChange = "layoutStyleChange",
    decoratorChanging = "decoratorChanging",
    decoratorActivating = "decoratorActivating",
    decoratorCreating = "decoratorCreating",
    decoratorRemoving = "decoratorRemoving"
}

export enum EGridComponent{
    grid = "grid",
    list = "list",
    card = "card",
    calendar = "calendar",
    board = "board",
    dashboard = "dashboard",
    none = "none"
}

export enum ELanguagesListType{
    mainLanguages = "mainLanguages",
    otherLanguages = "otherLanguages"
}

export enum EInlineEditType {
    entireTable = "entireTable"
}
export enum EInlineMode {
    alwaysOn = "alwaysOn"
}

export interface INotificationsCount {
    id: string,
    count: number
}
export interface IHeaderConfig {
    showCurrencySwitch?: boolean
    showCountrySwitch?: boolean
    showGlobalSearch?: boolean
    showTimer?: boolean
}

export interface IUserMenuConfig {
    showProfile?: boolean
}

export interface IMainMenuDecorator {
    id: string,
    noMenu: boolean,
    showInMenu: boolean, //fo tabs pages
    children?: IMainMenuDecorator[]
}

export interface IBoardsConfig {
    id: string,
    index?: string
    pageLink?: string
    children?: IBoardsConfig[]
    component?: string
    path?: string
    allowSourceColumns?: string[]
}

export interface ICurrency {
    value: number,
    currency: string
}

export type TWorkHours  = {from: number, to: number}

export interface IWorkTime {
    all? : TWorkHours[],
    workdays? : TWorkHours[],
    weekend? : TWorkHours[],
    1? : TWorkHours[],
    2? : TWorkHours[],
    3? : TWorkHours[],
    4? : TWorkHours[],
    5? : TWorkHours[],
    6? :TWorkHours[]
}

export interface ISearchSettings{
    id?: string|string[],
    userId?: string
    page? : number,
    size? : number,
    tid? : string[],
    searchTerm?: string,
    sort?: string[]
    agg?: string[]
    filters?: {[key: string]: (string|number|null)[]}
    systemFilters?: {[key: string]: (string|number|boolean|null)[]}
}

export interface IApiGeneratorConfig {
    name: string
    tag:string
    value:string,
    searchSettings? : {[key:string] : string|string[]}
}

export interface IFormDecoratorRow {
    field: string,
    type?: string,
    width?: number|string,
    disabled?: boolean //visible but inactive
    hidden?: boolean //invisible but active
    removed?: boolean //notPresent
    loading?: boolean
    localization?: ELanguagesListType|string[] //witch languages is visible in locale fields
    layout?: EFormLayout //only for locale field
    controls?: IFormDecoratorRow[][] //only for array field
    rowConfig?: {
        rowIndex: number,
        field: string,
        disabled?: boolean //visible but inactive
        hidden?: boolean //invisible but active
        removed?: boolean //notPresent
        required?: boolean //notPresent
        searchQuery?:  {[key: string] : string|string[]}
    }[] //only for array field
}

export interface IFormDecorator {
    id: string,
    parent: string
    settings: {
        name? : string
        label?: string
        width?: number|string
        disabled?: boolean
        visibleMode?: enums.EFormMode[]  //visible only in editMode or createMode
        hidden?: boolean //hide some tabs in complex forms
        controls: IFormDecoratorRow[][]
    }[]
}

export interface ISearchListItemConfig {
    name: string
    color?: string
    flex?: number
    align?: enums.ETextAlign
    brackets?: boolean
    locale?: boolean
    type?: "image"|"color"|"date"
    lookup?: string
    padding?: string
    width?: number
    prefix?: string
    suffix?: string
    fontSize?: number
    dateFormat?:string
}

export interface IRelatedField {
    name: string, //името на полето във формата
    value: string, //името на полето от данните които ще променята стойността на полето
    action?: string, //при какъв евент да се извърши действието (файла има 2 евента beforeUpload and onUpload)
    locale?: boolean, //дали полето на формата е с локализирана стойност (name_bg)
    itemLocale?: boolean //дали полето на данните е с локализирана стойност
    fillAllLocale?: boolean //да се попълнят всички локализирани полета на формата с всчики локализирани данни
}

export interface ISearchFormFieldConfig{
    clearBtn?: boolean
    api?: string //името на api функцията за четене на данни (да съвпада с име в apiConfig файла)
    listConfig?: ISearchListItemConfig[] //информация как да изглеждат данните в dropdown-а
    searchQuery?: {[key: string] : string|number|boolean|string[]} //предварително дефинирани филтри за търсене
    noEnterBtn?: boolean //без бутона "лупа"
    denyEmptySearch?: boolean
    fieldName?: {name: string, locale?: boolean} //името на самия контрол във формата - контрола подържа само string формат, но обикновенно заредените айтеми са object
    nameConfig?: ISearchListItemConfig[]
    hiddenFields?: {name: string, locale?: boolean}[]
    visualization?: boolean
    allowFreeTextInput?: boolean
    saveIfNotFound?: boolean
}

export interface ITreeFormFieldConfig{
    api?: string //името на api функцията за четене на данни (да съвпада с име в apiConfig файла)
    idField?: string
    fieldName?: {name: string, locale?: boolean} //името на самия контрол във формата - контрола подържа само string формат, но обикновенно заредените айтеми са object
    nameConfig?: ISearchListItemConfig[]
    parentField?: string
    searchQuery?: {[key: string] : string|number|boolean|string[]} //предварително дефинирани филтри за търсене
    multiselect?: boolean
    singleTreeSelect?: boolean
}

export interface IAdvanceSearchFormFieldConfig extends ISearchFormFieldConfig{
    searchComponent?: string
    elementRenderComponent?: string
    fields? : IFormConfigItem[]
}

export interface ISelectFormFieldConfig extends ISearchFormFieldConfig {
    lookup?: string,
    lookupVisualization?: string,
    allowLookupItems?: string[],
    multiselect?: boolean,
    fieldName?: {name: string, locale?: boolean} //името на самия контрол във формата - контрола подържа само string формат, но обикновенно заредените айтеми са object
    nameConfig?: ISearchListItemConfig[]
    maxTagCount?: number, //only for multiselect
    visualization?: boolean
    options?: {label: string|any, value: string|number}[]
}

export interface IBooleanFormFieldConfig extends ISearchFormFieldConfig {
    block?: boolean,
    checkLabel?:string
    uncheckLabel?:string
}

export interface IFileFormFieldConfig {
    accept?: string
    relatedFields?: IRelatedField[]
    actions: string[]
    hideButton?: boolean
    cls?: string //custom css class
    aspect?: number
    maxFileSize?: number //in MB
    quality?: number //from 0 to 1
    path?: string
    textFormat?: "text"|"html"|"md"
    convertType?: string,
    denyAutoUpload: boolean
}

export interface IFloatFormFieldConfig {
    min?: number
    max?: number
    step?: number
    precision?: number
    visualization?: boolean
    align?: string
    formatter?: (value: number) => string
}

export interface ICurrencyFormFieldConfig {
    min?: number
    max?: number
    step?: number
    precision?: number
    visualization?: boolean
    align?: string
    formatter?: (value: number) => string,
    lookup?: string,
    allowLookupItems?: string[],
    fieldName?: {name: string, locale?: boolean} //името на самия контрол във формата - контрола подържа само string формат, но обикновенно заредените айтеми са object
    nameConfig?: ISearchListItemConfig[]
    maxTagCount?: number, //only for multiselect
    options?: {label: string|any, value: string|number}[]
    valueField?: string
    dimensionField?: string
    dimensionPosition?: "left"|"right"
    dimensionDisabled?:boolean
    hideDimension?: boolean
}
export interface INumberVisualizationFieldConfig {
    precision?: number
    color?: string
    colorRules: {
        color: string,
        when: string // ">=0"|"<0"|=="one"|!="one"|=="[10,"one",true]"|!="[10,"one",true]"
    }[]
    valueField: {name: string}
    prefixField: {name: string, lookup?: string}
    suffixField: {name: string, lookup?: string}
    prefix: any
    suffix: any
    align?: string
}

export interface IGridFormFieldConfig {
    gridConfig: IGridConfig
}

export interface IGridPopupFormFieldConfig extends  ISearchFormFieldConfig{
    gridConfig: IGridConfig
    saveIfNotFound: boolean
}

export interface IArrayFormFieldConfig {
    fields: IFormConfigItem[]
    draggable?: boolean
    denyAddButton?: boolean
    denyRemoveButton?: boolean
    autoCreateFirstRow?: boolean
    noBorder?: boolean,
    showObjectLabel?: boolean
    labelsOnEveryRow?: boolean
    addBtnPlugins?: {name: string, label?: string, icon?: string, disabled?: boolean}[]
    denyRemoveRow?: number[]
}

export interface IObjectFormFieldConfig {
    fields: IFormConfigItem[]
    showBorder?: boolean,
    showObjectLabel?: boolean
}

export interface ICustomFieldConfig {
    denyAddButton?: boolean
    denyRemoveButton?: boolean
    ignoreRequired?: boolean
    disableControls?:boolean
    searchQuery?:  {[key: string] : string|string[]}
}

export interface IStringLocaleFieldConfig {
    languages?: ELanguagesListType
    includeLanguages?: string[]
    excludeLanguages?: string[]
    requiredLanguages?: string[]
    maxLength?: number
    align?: string
}

export interface IStringFieldConfig {
    maxLength?: number,
    visualization?: boolean
    align?: string
}
export interface IDateFieldConfig {
    showTime?: boolean
    multiselect?: boolean
    cellRender?: (current: any) => any
    disabledDate?: (current: any) => boolean
    disabledTime?: (data: any) => any
    timeFormat?: string
    dateFormat?: string
    minuteStep?: any
    pickerType?: "week"|"month"|"quarter"|"year"
}
export interface IGridFieldConfig {
    doNotSetFormValueOnChange?: boolean
}

export interface IFormConfigItem {
    name: string,
    type: enums.EFormFieldType|string,
    path?: string
    fullNamedPath?: (string|number)[], // array config only
    required?: boolean
    validator?: {name: enums.EFormValidator, path?: string}[]
    label?: string,
    help?: string,
    noLabel?: boolean,
    placeholder?: string,
    noPlaceholder?: boolean
    settings?: ISearchFormFieldConfig
        | IBooleanFormFieldConfig
        | ISelectFormFieldConfig
        | IFileFormFieldConfig
        | IAdvanceSearchFormFieldConfig
        | IGridFormFieldConfig
        | IGridPopupFormFieldConfig
        | IFloatFormFieldConfig
        | IArrayFormFieldConfig
        | IStringLocaleFieldConfig
        | IStringFieldConfig
        | ITreeFormFieldConfig
        | IDateFieldConfig
        | IGridFieldConfig
        | INumberVisualizationFieldConfig
        | ICustomFieldConfig
        | any
}

export interface IFormConfig {
    id: string
    fields: IFormConfigItem[]
}

export type localeSettings = {
    path?: string[],
    defaultInSearch? : string,
    defaultInGrid? : string,
    defaultInForm? : string
}|any

export interface IFormFullConfig extends IFormConfigItem {
    width: number|string
    lang?: string
    hidden?: boolean
    disabled?: boolean
    loading?: boolean
    removed?: boolean
    layout?: EFormLayout
    localization?: ELanguagesListType|string[]
}

export interface IFormSettingsSpecific {
    labelPath: string,
    gap?: number,
    width?: number,
    titleEdit?: string
    titleEditField?: string
    titleCreate?: string
    labelWrapper?: boolean
    ignoreChangedFields?: string[]
}

export interface IFormSettings {
    form: FormInstance<any>,
    config: IFormConfig
    decorator: IFormDecorator,
    settings: IFormSettingsSpecific,
    transformFn: {
        inputTransform: (data: any) => object,
        outputTransform: (data: any) => object,
    }
    api: any
    item: any
    parentItem?: any
    defaultValue?: any
}

export interface IFormItemProps {
    field: IFormFullConfig
    labelPath: string
    styles?: any
    validator?: any
    validators?: any
    item: any
    config: any
    form: any
    api?:any
    decorator?:IFormDecorator
    onSelect: (data: any, name?: string, options?: any) => void
    onChange: (data: any, name?: string, options?: any) => void
    onUpload: (data: any, name?: string, options?: any) => void
    onBlur: (data: any, name?: string, options?: any) => void
    onFocus: (data: any, name?: string, options?: any) => void
    onBeforeSearch: (data: any, name?: string, options?: any) => { data: any }
    onAfterSearch: (data: any, name?: string, options?: any) => { data: any }
    onBeforeCreateNew: (data: any, name?: string, options?: any) => { data: any }
    onAfterCreateNew: (data: any, name?: string, options?: any) => { data: any }
    onClear: (name?: string, options?: any) => void
    inlineEdit?: InlineEdit
    fullNamedPath?: (string|number)[]
}

export interface IGridAvatarFieldConfig {
    textField?: string
    linkType?: string
    linkData?: string
    noLink?: boolean
    imageField?: string
    descriptionField?: string
    iconPath?: string
    shape?: "circle"|"square"
    size?: number,
    showDefaultOnMissing?: boolean
}

export interface IGridBooleanFieldConfig {
    textField?: string
    color?: string
    rounded?: string
    lookup?: string,
    showDefaultOnMissing?: boolean
}
export interface IGridCurrencyFieldConfig {
    textField?: string
    noWrap?: boolean,
    showDefaultOnMissing?: boolean
}
export interface IGridDateFieldConfig {
    textField?: string,
    showHours?: boolean
    format?: string,
    showDefaultOnMissing?: boolean
}

export interface IGridArrayFieldConfig {
    noSubLabels?: boolean
    textField?: string|string[]
    noWrap?: boolean
    delimiter?: string
    separator?: string
    tags?: boolean
    fields?: any[]
    prefix?: string
    suffix?: string,
    showDefaultOnMissing?: boolean
    lookup?: string
}

export interface IGridObjectFieldConfig {
    fields: any[]
    noSubLabels?: boolean,
    showDefaultOnMissing?: boolean
}

export interface IFileSizeFieldConfig {
    textField?: string
    noWrap?: boolean,
    showDefaultOnMissing?: boolean
}

export interface IHighlightFieldConfig {
    textField?: string
    locale?: localeSettings,
    showDefaultOnMissing?: boolean
    html?: boolean
}

export interface IGridImageFieldConfig {
    textField?: string,
    height?: number,
    showDefaultOnMissing?: boolean
}

export interface IGridLinkFieldConfig {
    textField?: string
    idField?: string
    linkType?: string
    linkData?: string
    noWrap?: boolean
    locale?: localeSettings
    linkInNewTab?: boolean
    iconPath?: string
    rounded?: boolean,
    showDefaultOnMissing?: boolean
}

export interface IGridNumberFieldConfig {
    textField?: string
    noWrap?: boolean
    decimal?: number|string
    prefix?: string
    suffix?: string,
    showDefaultOnMissing?: boolean
}

export interface IGridRangeFieldConfig {
    textField?: string,
    showDefaultOnMissing?: boolean
}

export interface IGridStringFieldConfig {
    textField?: string
    locale?: localeSettings
    lookup?: string
    noWrap?: boolean
    prefix?: string
    suffix?: string,
    showDefaultOnMissing?: boolean
    html?: boolean
}


export interface IGridTagFieldConfig {
    textField?: string
    colorField?: string
    locale?: localeSettings
    lookup?: string
    color? : string
    rounded? : boolean
    iconPath?: string,
    showDefaultOnMissing?: boolean
}


export interface IGridWorkTimeFieldConfig {
    textField?: string,
    showDefaultOnMissing?: boolean
}

export interface IGridColumnConfig {
    name           : string,
    type           : string,
    group?         : string,
    label?         : string,
    sorting?       : boolean,
    sortingField?  : string,
    defaultSort?   : enums.EGridSorting,
    noLabel?       : boolean
    settings?      : 
        IGridAvatarFieldConfig |
        IGridBooleanFieldConfig |
        IGridCurrencyFieldConfig |
        IGridDateFieldConfig |
        IFileSizeFieldConfig |
        IHighlightFieldConfig |
        IGridImageFieldConfig |
        IGridLinkFieldConfig |
        IGridNumberFieldConfig |
        IGridRangeFieldConfig |
        IGridStringFieldConfig |
        IGridWorkTimeFieldConfig |
        IGridArrayFieldConfig |
        IGridObjectFieldConfig |
        IGridTagFieldConfig
    componentPath?: string
    filters? : {
        multiSelect?: boolean,
        search?: boolean,
        menuType? : "menu"|"tree",
        aggField?: string,
        aggName?: string,
        source?: string[],
        textField?: string,
        valueField?: string,
        lookup? : string,
        component?: string,
        componentPath?: string
        icon?: string,
    }
}

export interface IGridCellConfig {
    gridId?: string,
    fieldConfig: IGridColumnConfig,
    item: any,
    itemProcessing?:string|null
    api?: any
    events?: {
        onEdit: (data: any) => void
        onRemove: (data: any) => void
        onCancel: () => void
        onConfirm: () => void
        onFilterClick: () => void,
        onChange: (data: any) => void,
        onCreate: (data: any) => void,
        onUpdateItem: (data: any) => void
    }
    settings?: {
        labelPath: string
        indexName?: string
        inlineEdit?: InlineEdit
    },
    editMode?: boolean
    createMode?: boolean
    activeItem?: any
}

export interface IGridFullConfig extends IGridColumnConfig{
    width?: number
    fixed?: string
    align?: string
    wrap?: boolean
    disabledByDefault?: boolean
}

export interface IGridConfig {
    id: string
    index: string
    parent?: string
    columns: IGridColumnConfig[]
    rowSelection? : {
        type: "checkbox"|"radio"
        selectedRowKeys?: string[]
    }
}

export interface IGridDecoratorColumn {
    field: string,
    width?: number,
    align?: string,
    fixed?: string
}

export interface IGridSettings {
    config: IGridConfig,
    decorators?:  IGridDecorator[],
    activeDecorator?:  IGridDecorator,
    selectedRows?:  string[],
    settings: {
        labelPath: string,
        rowKey?: string,
        noScroll?: boolean
        inlineEdit?: InlineEdit
        //board settings
        id?: string
        apiConfig?: any,
        boardConfig: IBoardsConfig[],
        columnWidth?: number,
        dynamicColumns?: boolean,
        //use bellow settings only if dynamicColumns === true
        columnSettings?: IGridControllerSettings,
        sortField?: string,
        titleField?: string,
        addStaticColumn?: boolean,
        denyRearrange?: boolean,
        denyCreate?: boolean
    }
    parentItem?: any
    parentItemType?: string
    api: any
    resources: any
}

export interface ICardConfig {
    name           : string,
    label?         : string,
    render?        : string,
    noLabel?       : boolean
}

export interface ICardDecoratorRow {
    field: string
}

export interface ICardDecorator {
    title?: string
    extra?: string
    styles?: React.CSSProperties
    fields: ICardDecoratorRow[][]
    layout?: "horizontal"|"vertical"
    border?: boolean
}

export interface ISingleTabConfig {
    name: string
    label?: string
    noLink?: boolean
    components: {
        id?: string
        name: string
        path?:string
        visible? : {
            name: string
            value: string
        }
    }[]
}

export interface ITabsConfig {
    id: string
    noTitle?: boolean
    config: ISingleTabConfig[]
}

export interface ITabsDecorator {
    id: string,
    disabled?: boolean
    hidden?: boolean
    controls?: ITabsDecorator[]
}

export interface ITabProps {
    data: {id: string}
    name: string
    form: IFormSettings
    item?: any,
    card: {
        config: IGridConfig,
        decorator: ICardDecorator
        settings: {
            labelPath: string
        }
    }
    apiConfig: IApiGeneratorConfig[],
    cardDecorator: ICardDecorator,
    gridConfig:  IGridConfig,
    formConfig: IFormConfig,
    formDecorator: IFormDecorator,
    defaultFormValue : any,
    useFormResources : any,
    useGridResources? : any,
    settings: {
        labelPath: string
    },
    formSettings: {
        width? : number
    }
    tabResources: any
    tabState?: any
    onStateChange: any
}

export interface ITabSettings {
    item?: any
    itemId?: string;
    config: ITabsConfig,
    decorator: ITabsDecorator[],
    apiConfig: IApiGeneratorConfig[],
    settings: {
        labelPath: string
        tabsPath?: string
        indexName?:string
        defaultTab: string
        titleField?: {
            name :string,
            locale?: boolean,
            lang?: enums.ELanguages
        }
    }
    tabs: any
    useTabResources?: any
}

export interface IComponentList {
    path: string
    name: string
    component?: string
}

export interface InlineEdit {
    name: string,
    type?: EInlineEditType,
    mode?: EInlineMode
    formInitPath?: string[]
}

export interface IGridProps {
    config : IGridConfig
    decorators?: IGridDecorator[],
    activeDecorator?: IGridDecorator,
    itemProcessing?: string|null
    selectedRows?: string[]
    settings : {
        id?: string | undefined;
        labelPath: string,
        addRowLabelPath?: string,
        rowKey?: string,
        noScroll?: boolean,
        noPagination?: boolean
        simplePagination?: boolean //use when count is unknown
        reportName?: string
        inlineEdit?: InlineEdit,
        cardLayout? :{
            xs?: number,
            sm?: number,
            md?: number,
            lg?: number, 
            xl?: number,
            xxl?:number,
            gutter?: number[]|any
        }
        //board settings
        apiConfig?: any,
        boardConfig?: IBoardsConfig[],
        dashboardConfig?: IDashboardConfig[],
        columnWidth?: number,
        dynamicColumns?: boolean,
        //use bellow settings only if dynamicColumns === true
        columnSettings?: IGridControllerSettings,
        sortField?: string,
        titleField?: string,
        addStaticColumn?: boolean,
        denyRearrange?: boolean,
        denyCreate?: boolean
    }
    loading?: boolean,
    data: {
        count: number,
        items: any[]
        searchSettings: any,
        filters?: any
    }
    events?: {
        onEdit: (data: any) => void
        onRemove: (data: any) => void
        onCancel: () => void
        onConfirm: () => void
        onFilterClick: () => void,
        onChange: (data: any) => void,
        onCreate: (data: any) => void,
        onUpdateItem: (data: any) => void
    }|any
    aggs?: any,
    resources?: {
        rowClassName?: (record: any, index: number) => string
        expandable?: {
            expandedRowRender?: (data: any) => any,
            rowExpandable?: (data:any) => boolean,
        }
    },
    formConfig?: any,
    editMode?:boolean
    createMode?: boolean
    activeItem?: any,
    inlineEdit?: InlineEdit
    setItems? : any
    api? : any
    parentItem?: any
}

export interface IApiConfig {
    name: string
    tag: string
    value: string
}

export interface IGridSearchSettings {
    systemFilters?: any
    filters?: any
}

export interface IValidator {
    value?: number|string
    t: any,
    item?:any
    parentItem?:any
    field?: string
    api?: any
    query?: any
}

export interface IBoardSettings {
    id?: string
    apiConfig?: any,
    boardConfig: IBoardsConfig[],
    columnWidth?: number,
    dynamicColumns?: boolean,
    //use bellow settings only if dynamicColumns === true
    columnSettings?: IGridControllerSettings,
    sortField?: string,
    titleField?: string,
    addStaticColumn?: boolean,
    denyRearrange?: boolean,
    denyCreate?: boolean
}
export interface IGridControllerSettings {
    visible?: boolean //use only for GridPopup field when saveIfNotFound field is true
    items?: any[]  //list of items presented on grid -> use in offline mode
    parentItem?: any //grid items elements may be part of of the parentItem
    parentItemType?: string //use in offline mode
    gridComponent?: EGridComponent
    formDecorator?: IFormDecorator
    formConfig?: IFormConfig
    defaultFormValue?: any
    defaultGridValue?: {
        selectedRows?: string[]
    }
    gridSearchSettings?: ISearchSettings
    gridDecorator?: IGridDecorator,
    defaultGridDecorator?: IGridDecorator,
    gridCustomColumns?: {field:string, width?:number}[],
    gridConfig:  IGridConfig,
    apiConfig:  IApiGeneratorConfig[],
    customApi?: {[key: string]: any}
    dashboardConfig?:  IDashboardConfig[],
    listDecorator?: IListDecorator
    settings: {
        labelPath: string,
        addRowLabelPath?: string,
        indexName?: string,
        keepPopupOpen?: boolean,
        tabConfig?: {
            itemId?: string,
            tabId?: string,
            name?: string
        }
    }
    formSettings?: {
        ignoreTransformFields?: string[]
        width?: number,
        gap? :number
        titleEdit?: string
        titleCreate?: string
    }
    gridSettings?: IGridProps["settings"]
    boardSettings?: IBoardSettings
    useFormResources?: any
    useGridResources?: any
    useControllerResources?: any
    reactComponents?: React.FC[]
}

export interface IGridTabProps extends IGridControllerSettings {
    name: string
    tabId: string
    itemId: string
}

export interface ITabResourceProps<T> {
    loading?: boolean
    item: T,
    config: ITabsConfig,
    decorator: ITabsDecorator[],
    settings: {
        labelPath: string
        tabsPath?: string
        defaultTab: string
    }
    tabsState: any
    tabs: any
    events: any,
    setDecorator: any
    setConfig: any
}

export interface IDashboardConfig {
    name?: string
    help?: boolean
    width?: number
    equalHeight?: boolean
    reports: {
        name: EReports,
        path?: string
        width?: number
    }[]
}
export interface IListDecorator {
    id?: string
    columns: {
        flex: 1|0
        align: "left"|"right"|"center"
        verticalAlign: "top"|"center"|"bottom",
        rows: {field: string, type?:string}[][]
    }[]
}
